import { useMemo, useRef, useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { NewHireJourney } from "models/v2/newHireJourney";
import { Link as RouterLink } from "react-router";
import {
  AlertDialog, AlertDialogBody, AlertDialogContent, AlertDialogFooter,
  AlertDialogHeader, AlertDialogOverlay, Box, Button, HStack, Icon,
  IconButton, Link, Menu, MenuButton, MenuItem, MenuList, Portal, Text, Tooltip, VStack, useDisclosure, useToast,
} from "@chakra-ui/react";
import { FiMoreVertical, FiTrash2 } from "react-icons/fi";
import { LuUserX } from "react-icons/lu";
import { pendingNewHirePath } from "helpers/url/pages/v1";
import { basicShortDate } from "helpers/time";
import { Text12 } from "components/Text";
import ControlledDataTable from "components/Table/ControlledDataTable";
import { useDeletePendingNewHireJourney, usePendingNewHireJourneysListQuery } from "features/PendingNewHire/hooks";
import useCaminoStore from "hooks/useCaminoStore";
import ROLE from "models/role";
import moment from "moment";
import { PrimaryButton } from "components/Button";

const columnHelper = createColumnHelper<NewHireJourney>();

type DeleteMode = "journey" | "user";

function DeleteJourneyMenu({ journey }: { journey: NewHireJourney }) {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<DeleteMode>("journey");
  const { mutate, isPending } = useDeletePendingNewHireJourney();
  const currentUser = useCaminoStore((s) => s.currentUser);

  const canDelete = !!currentUser?.permission?.some(
    (p) => p === ROLE.COMPANY_ADMIN || p === ROLE.PEOPLE_TEAM,
  );

  if (!canDelete) return null;

  const newHireName = journey.newHire?.fullName ?? "this new hire";

  const openConfirm = (next: DeleteMode) => {
    setMode(next);
    onOpen();
  };

  const handleConfirm = () => {
    mutate(
      { journeyId: journey.id, destroyUser: mode === "user" },
      {
        onSuccess: () => {
          onClose();
          toast({
            title: mode === "user" ? "User and journey deleted" : "Journey deleted",
            status: "success",
          });
        },
        onError: () => {
          toast({ title: "Could not delete. Please try again.", status: "error" });
        },
      },
    );
  };

  const copy = mode === "user"
    ? `Permanently delete ${newHireName} and their pending journey. This cannot be undone.`
    : `Permanently delete this pending journey for ${newHireName}. The user record will remain. This cannot be undone.`;

  return (
    <>
      <Menu placement="bottom-end">
        <MenuButton
          as={IconButton}
          aria-label="Journey actions"
          icon={<FiMoreVertical size={14} />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
        />
        <Portal>
          <MenuList>
            <MenuItem
              color="red.600"
              icon={<Icon as={FiTrash2} boxSize={3.5} />}
              onClick={() => openConfirm("journey")}
            >
              Delete journey
            </MenuItem>
            <MenuItem
              color="red.600"
              icon={<Icon as={LuUserX} boxSize={3.5} />}
              onClick={() => openConfirm("user")}
            >
              Delete user and journey
            </MenuItem>
          </MenuList>
        </Portal>
      </Menu>

      <AlertDialog isOpen={isOpen} leastDestructiveRef={cancelRef} onClose={onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {mode === "user" ? `Delete ${newHireName}?` : "Delete pending journey?"}
            </AlertDialogHeader>
            <AlertDialogBody>{copy}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose} variant="outline" isDisabled={isPending}>
                Cancel
              </Button>
              <Button colorScheme="red" ml={3} onClick={handleConfirm} isLoading={isPending}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}

interface PendingNewHireJourneysTableProps {
  emptyStateComponent?: React.ReactNode;
}

export default function PendingNewHireJourneysTable({ emptyStateComponent }: PendingNewHireJourneysTableProps = {}) {
  const { data: pendingJourneys = [], isLoading } = usePendingNewHireJourneysListQuery();

  const columns = useMemo(() => [
    // Name & Title Column
    columnHelper.accessor((row) => row, {
      id: "name",
      header: "Name / Title",
      cell: ({ getValue }) => {
        const journey = getValue();
        return (
          <VStack align="start" spacing={0}>
            <Link
              as={RouterLink}
              to={pendingNewHirePath(journey)}
              fontWeight="medium"
              fontSize="14px"
              _hover={{ textDecoration: "underline" }}
            >
              {journey?.newHire?.fullName}
            </Link>
            {journey?.newHire?.title && (
              <Text fontSize="12px" color="gray.600">
                {journey?.newHire?.title}
              </Text>
            )}
          </VStack>
        );
      },
      meta: {
        sx: {
          width: "250px",
          minWidth: "250px",
          maxWidth: "250px",
        },
      },
      enableSorting: false,
    }),

    // Start Date Column
    columnHelper.accessor((row) => row.startDate, {
      id: "startDate",
      header: "Start Date",
      cell: ({ getValue }) => {
        const date = getValue();
        if (date) return <Text12>{basicShortDate(date)}</Text12>;
        return (
          <Tooltip
            label="The ATS didn't include a start date. Confirm with the hiring manager and set the date before activating the journey."
            placement="top"
            hasArrow
            openDelay={300}
          >
            <Text12 color="orange.600" fontStyle="italic" cursor="default">
              Not provided
            </Text12>
          </Tooltip>
        );
      },
      meta: {
        sx: {
          width: "120px",
          minWidth: "120px",
          maxWidth: "120px",
        },
      },
      enableSorting: false,
    }),

    // Relative Start Date Column
    columnHelper.accessor((row) => row.startDate, {
      id: "relativeStartDate",
      header: "",
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return <Text12 color="gray.400">—</Text12>;

        const startMoment = moment(date);
        const now = moment();
        const isPast = startMoment.isBefore(now, "day");
        const isToday = startMoment.isSame(now, "day");

        const relativeText = isToday ? "Today" : startMoment.fromNow();

        return (
          <Box
            px={2}
            py={1}
            borderRadius="md"
            bg={isPast ? "red.50" : "transparent"}
            display="inline-block"
          >
            <Text12 color={isPast ? "red.600" : "gray.900"} fontWeight={isPast ? "semibold" : "normal"}>
              {relativeText}
            </Text12>
          </Box>
        );
      },
      meta: {
        sx: {
          width: "140px",
          minWidth: "140px",
          maxWidth: "140px",
        },
      },
      enableSorting: false,
    }),

    // Actions Column
    columnHelper.accessor((row) => row, {
      id: "action",
      header: "",
      cell: ({ getValue }) => {
        const journey = getValue();
        return (
          <HStack spacing={2} justify="flex-end">
            <PrimaryButton
              as={RouterLink}
              to={pendingNewHirePath(journey)}
              size="sm"
            >
              Kick Off Onboarding
            </PrimaryButton>
            <DeleteJourneyMenu journey={journey} />
          </HStack>
        );
      },
      meta: {
        sx: {
          width: "240px",
          minWidth: "240px",
          maxWidth: "240px",
        },
      },
      enableSorting: false,
    }),
  ], []);

  if (!isLoading && pendingJourneys.length === 0 && emptyStateComponent) {
    return <>{emptyStateComponent}</>;
  }

  return (
    <ControlledDataTable
      data={pendingJourneys}
      columns={columns}
      isLoading={isLoading}
    />
  );
}
